import { createFormikField, PrimaryButton, SecondaryButton, theme } from '@cmg/common';
import React from 'react';
import ReactSelect, {
  components,
  GroupBase,
  OptionProps,
  type ValueContainerProps,
} from 'react-select';

import {
  checkboxStyles,
  SButtonsContainer,
  SComponentContainer,
  SComponentLabel,
  SIconContainer,
  SIndentOptionContainer,
  SLabel,
  SOptionContainer,
  SSelectAllContainer,
  StyledIcon,
} from './CheckboxTreeSelect.styles';

type TreeOption = {
  label: string;
  value: string;
  children?: TreeOption[];
};

type FlattenedOption = {
  label: string;
  value: string;
  parent?: string;
  isChild?: boolean;
};

export type CheckboxTreeSelectProps = {
  options: TreeOption[];
  testId?: string;
  name?: string;
  label?: React.ReactNode;
  withMargin?: boolean;
  width?: number;
  resetValues: string[];
  onChange?: ((value: string[]) => void) | undefined;
  value?: string[];
  open?: boolean;
};

/**
 * Flattens a tree of options into a flat array with parent/child info.
 */
function flattenOptions(options: TreeOption[], parent?: string): FlattenedOption[] {
  return options.flatMap(opt => [
    { label: opt.label, value: opt.value, parent, isChild: !!parent },
    ...(opt.children ? flattenOptions(opt.children, opt.value) : []),
  ]);
}

/**
 * Recursively gets all descendant values for a given option.
 */
function getAllDescendantValues(option: TreeOption): string[] {
  if (!option.children || option.children.length === 0) {
    return [];
  }
  return option.children.reduce<string[]>(
    (acc, child) => [...acc, child.value, ...getAllDescendantValues(child)],
    []
  );
}

/**
 * Finds an option by its value in a tree of options.
 */
function findOptionByValue(options: TreeOption[], value: string): TreeOption | undefined {
  for (const opt of options) {
    if (opt.value === value) {
      return opt;
    }
    if (opt.children) {
      const found = findOptionByValue(opt.children, value);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

/**
 * Adds parent (header) values if all their children are selected.
 */
function addHeaderValues(values: string[], options: TreeOption[]): string[] {
  const result = new Set(values);

  for (const option of options) {
    if (option.children && option.children.length > 0) {
      const allChildrenSelected = option.children.every(child => values.includes(child.value));
      if (allChildrenSelected) {
        result.add(option.value);
      }
    }
  }

  return Array.from(result);
}

/**
 * Determines which value was changed (added or removed) between two arrays.
 */
function getChangedValue(
  prevValues: string[],
  newValues: string[]
): { changedValue?: string; checked?: boolean } {
  const added = newValues.find(val => !prevValues.includes(val));
  if (added !== undefined) {
    return { changedValue: added, checked: true };
  }
  const removed = prevValues.find(val => !newValues.includes(val));
  if (removed !== undefined) {
    return { changedValue: removed, checked: false };
  }
  return {};
}

/**
 * Handles selection/deselection of a parent option and its descendants.
 */
function handleParentChange(
  checked: boolean,
  changedValue: string,
  newValues: string[],
  options: TreeOption[]
): string[] {
  const option = findOptionByValue(options, changedValue);
  if (!option || !option.children || option.children.length === 0) {
    return newValues;
  }
  const descendants = getAllDescendantValues(option);
  if (checked) {
    return Array.from(new Set([...newValues, ...descendants, changedValue]));
  } else {
    return newValues.filter(val => val !== changedValue && !descendants.includes(val));
  }
}

/**
 * Handles selection/deselection of a child option and updates its parent if needed.
 */
function handleChildChange(
  changedOption: FlattenedOption,
  newValues: string[],
  flatOptions: FlattenedOption[]
): string[] {
  if (!changedOption.isChild || !changedOption.parent) {
    return newValues;
  }
  const siblings = flatOptions.filter(opt => opt.parent === changedOption.parent && opt.isChild);
  const siblingsSelected = siblings.filter(sib => newValues.includes(sib.value));
  let updated = [...newValues];
  if (siblingsSelected.length === siblings.length) {
    updated = Array.from(new Set([...updated, changedOption.parent]));
  } else {
    updated = updated.filter(val => val !== changedOption.parent);
  }
  return updated;
}

const CheckboxOption =
  (treeOptions: TreeOption[]) =>
  (props: OptionProps<FlattenedOption, true, GroupBase<FlattenedOption>>) => {
    const indent = props.data.isChild ? 24 : 0;
    const { value } = props.selectProps;
    let isMixed = false;
    let isAllSelected = false;
    let hasChildren = false;

    if (!props.data.isChild) {
      const option = findOptionByValue(treeOptions, props.data.value);
      if (option && option.children && option.children.length > 0) {
        const descendants = getAllDescendantValues(option);
        const selectedCount = descendants.filter(
          d => Array.isArray(value) && value.some((v: FlattenedOption) => v.value === d)
        ).length;
        if (descendants.length > 0 && selectedCount > 0 && selectedCount < descendants.length) {
          isMixed = true;
        }
        if (descendants.length > 0 && selectedCount === descendants.length) {
          isAllSelected = true;
        }
        if (descendants.length > 0) {
          hasChildren = true;
        }
      }
    }

    return (
      <components.Option {...props}>
        <SOptionContainer
          role="checkbox"
          data-testid={`offeringType-filter-${props.data.value}`}
          data-test-id={`offeringType-filter-${props.data.value}`}
        >
          <SIndentOptionContainer $indent={indent}>
            <SIconContainer
              isChecked={(props.isSelected && !hasChildren) || isAllSelected || isMixed}
            >
              {(props.isSelected || isAllSelected) && !isMixed && (
                <StyledIcon name="check" variant="solid" size="xs" />
              )}
              {isMixed && <StyledIcon name="minus" variant="solid" size="xs" />}
            </SIconContainer>
            <SLabel>{props.label}</SLabel>
          </SIndentOptionContainer>
        </SOptionContainer>
      </components.Option>
    );
  };

const SelectAllOption = ({
  isAllSelected,
  isIndeterminate,
  onClick,
}: {
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onClick: () => void;
}) => {
  let ariaChecked: 'true' | 'false' | 'mixed' = 'false';

  if (isAllSelected) {
    ariaChecked = 'true';
  } else if (!isAllSelected && isIndeterminate) {
    ariaChecked = 'mixed';
  } else {
    ariaChecked = 'false';
  }

  return (
    <SSelectAllContainer onClick={onClick} role="checkbox" aria-checked={ariaChecked}>
      <SIconContainer isChecked={isAllSelected || isIndeterminate}>
        {isAllSelected && <StyledIcon name="check" variant="solid" size="xs" />}
        {!isAllSelected && isIndeterminate && <StyledIcon name="minus" variant="solid" size="xs" />}
      </SIconContainer>
      <SLabel>Select All</SLabel>
    </SSelectAllContainer>
  );
};

const customValueContainer =
  (
    flatOptions: FlattenedOption[],
    setMenuIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    menuIsOpen: boolean,
    value: string[]
  ) =>
  (props: ValueContainerProps<FlattenedOption, true, GroupBase<FlattenedOption>>) => {
    const selectedLabels = flatOptions
      .filter(
        opt =>
          value.includes(opt.value) &&
          (opt.isChild || (!opt.isChild && !flatOptions.some(o => o.parent === opt.value)))
      )
      .map(opt => opt.label)
      .join(', ');
    return (
      <components.ValueContainer
        {...props}
        innerProps={{ ...props.innerProps, onClick: () => setMenuIsOpen(!menuIsOpen) }}
      >
        {selectedLabels}
      </components.ValueContainer>
    );
  };

type MenuListProps = {
  isAllSelected: boolean;
  isIndeterminate: boolean;
  setStagedValue: React.Dispatch<React.SetStateAction<string[]>>;
  allValues: string[];
  handleApply: () => void;
  handleReset: () => void;
  children: React.ReactNode;
  options: TreeOption[];
};

const MenuList: React.FC<MenuListProps> = ({
  isAllSelected,
  isIndeterminate,
  setStagedValue,
  allValues,
  handleApply,
  handleReset,
  children,
  options,
}) => (
  <React.Fragment>
    <SelectAllOption
      isAllSelected={isAllSelected}
      isIndeterminate={isIndeterminate}
      onClick={() => {
        if (isAllSelected) {
          setStagedValue([]);
        } else {
          setStagedValue(addHeaderValues(allValues, options));
        }
      }}
    />
    {children}
    <SButtonsContainer>
      <PrimaryButton onClick={handleApply}>Apply</PrimaryButton>
      <SecondaryButton onClick={handleReset}>Reset</SecondaryButton>
    </SButtonsContainer>
  </React.Fragment>
);

export const CheckboxTreeSelect: React.FC<CheckboxTreeSelectProps> = (props): JSX.Element => {
  const {
    options,
    testId,
    label,
    withMargin,
    width,
    resetValues,
    onChange,
    value = [],
    open = false,
  } = props;

  const [menuIsOpen, setMenuIsOpen] = React.useState(open);
  const [stagedValue, setStagedValue] = React.useState<string[]>(addHeaderValues(value, options));
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Tracking the open state so that we know to close the menu when clicking outside of it
  React.useEffect(() => {
    if (!menuIsOpen) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setMenuIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuIsOpen]);

  React.useEffect(() => {
    if (!menuIsOpen) {
      return;
    }
    setStagedValue(addHeaderValues(value, options));
  }, [menuIsOpen, options, value]);

  const omittedValues = ['ipos', 'followOns', 'blocks'];
  const flatOptions = flattenOptions(options);
  const allValues = flatOptions
    .filter(opt => !omittedValues.includes(opt.value))
    .map(opt => opt.value);

  const isAllSelected = allValues.length > 0 && allValues.every(v => stagedValue.includes(v));
  const isIndeterminate = !isAllSelected && stagedValue.length > 0;

  const handleApply = () => {
    if (onChange) {
      onChange(stagedValue.filter(v => !omittedValues.includes(v)));
    }
    setMenuIsOpen(false);
  };

  const handleReset = () => {
    setStagedValue(addHeaderValues(resetValues, options));
  };

  const handleChange = (selected: readonly FlattenedOption[], _actionMeta: any) => {
    const prevValues = stagedValue;
    const newValues = selected.map(opt => opt.value);

    const { changedValue, checked } = getChangedValue(prevValues, newValues);

    if (!changedValue) {
      setStagedValue(newValues);
      return;
    }

    const option = findOptionByValue(options, changedValue);
    const changedOption = flatOptions.find(opt => opt.value === changedValue);

    if (option && option.children && option.children.length > 0) {
      setStagedValue(handleParentChange(!!checked, changedValue, newValues, options));
      return;
    }
    if (changedOption && changedOption.isChild && changedOption.parent) {
      setStagedValue(handleChildChange(changedOption, newValues, flatOptions));
      return;
    }

    setStagedValue(newValues);
  };

  return (
    <SComponentContainer
      ref={rootRef}
      data-test-id={testId}
      data-testid={testId}
      $withMargin={withMargin}
      $width={width}
    >
      {label && <SComponentLabel>{label}</SComponentLabel>}
      <ReactSelect
        options={flatOptions}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable={false}
        isSearchable={false}
        components={{
          Option: CheckboxOption(options),
          ValueContainer: customValueContainer(flatOptions, setMenuIsOpen, menuIsOpen, value),
          MenuList: props => (
            <MenuList
              isAllSelected={isAllSelected}
              isIndeterminate={isIndeterminate}
              setStagedValue={setStagedValue}
              allValues={allValues}
              handleApply={handleApply}
              handleReset={handleReset}
              options={options}
            >
              <components.MenuList {...props} />
            </MenuList>
          ),
        }}
        value={flatOptions.filter(opt => stagedValue.includes(opt.value))}
        onChange={handleChange}
        getOptionLabel={opt => opt.label}
        getOptionValue={opt => opt.value}
        styles={checkboxStyles(theme)}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
      />
    </SComponentContainer>
  );
};

export const CheckboxTreeSelectField = createFormikField<
  any,
  any,
  HTMLInputElement,
  CheckboxTreeSelectProps
>(CheckboxTreeSelect);
